/* Personagens procedurais — estilos nomeados (bear, mechanic, neonrider, default). */
import * as THREE from 'three'

const texLoader = new THREE.TextureLoader()
const texCache = new Map()

function loadSkinTexture(url) {
    if (!texCache.has(url)) {
        const t = texLoader.load(url)
        if (THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace
        t.flipY = true
        texCache.set(url, t)
    }
    return texCache.get(url)
}

function toon(color, extra = {}) {
    return new THREE.MeshToonMaterial({ color, ...extra })
}

function box(g, w, h, d, x, y, z, mat) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat)
    m.position.set(x, y, z)
    m.castShadow = true
    g.add(m)
    return m
}

function sphere(g, r, x, y, z, mat, segs = 12) {
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, segs, segs), mat)
    m.position.set(x, y, z)
    m.castShadow = true
    g.add(m)
    return m
}

function styleDefault(g, bodyMat, accent, dark) {
    box(g, 0.45, 0.5, 0.35, 0, 0.55, -0.05, bodyMat)
    sphere(g, 0.22, 0, 0.95, -0.02, bodyMat)
    const cap = new THREE.Mesh(
        new THREE.SphereGeometry(0.23, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55),
        accent
    )
    cap.position.set(0, 1.0, -0.02)
    cap.castShadow = true
    g.add(cap)
    box(g, 0.12, 0.12, 0.35, -0.32, 0.55, 0.15, bodyMat)
    box(g, 0.12, 0.12, 0.35, 0.32, 0.55, 0.15, bodyMat)
    box(g, 0.1, 0.1, 0.1, -0.25, 0.5, 0.35, bodyMat)
    box(g, 0.1, 0.1, 0.1, 0.25, 0.5, 0.35, bodyMat)
    box(g, 0.16, 0.16, 0.4, -0.14, 0.28, 0.15, dark)
    box(g, 0.16, 0.16, 0.4, 0.14, 0.28, 0.15, dark)
    box(g, 0.14, 0.1, 0.22, -0.14, 0.18, 0.4, dark)
    box(g, 0.14, 0.1, 0.22, 0.14, 0.18, 0.4, dark)
}

/** Vital — urso grandão, mal cabe no conversível. */
function styleBear(g, bodyMat, accent, dark) {
    const fur = bodyMat
    const snout = new THREE.MeshStandardMaterial({ color: 0xd9a77d, roughness: 0.9 })
    const nose = new THREE.MeshStandardMaterial({ color: 0x171312, roughness: 0.3 })
    const eyeWhite = new THREE.MeshStandardMaterial({ color: 0xfff3df, roughness: 0.45 })
    const eye = new THREE.MeshStandardMaterial({ color: 0x17120f, roughness: 0.2 })
    // broad, rounded shoulders and belly give Vital a heavy, plush silhouette
    sphere(g, 0.66, 0, 0.9, -0.12, fur, 20)
    sphere(g, 0.57, 0, 0.72, 0.13, fur, 20)
    sphere(g, 0.49, 0, 1.62, -0.04, fur, 20)
    sphere(g, 0.16, -0.38, 2.0, -0.1, fur, 10)
    sphere(g, 0.16, 0.38, 2.0, -0.1, fur, 10)
    sphere(g, 0.25, 0, 1.48, 0.36, snout, 16)
    sphere(g, 0.09, 0, 1.53, 0.57, nose, 12)
    for (const x of [-0.19, 0.19]) {
        sphere(g, 0.09, x, 1.73, 0.345, eyeWhite, 12)
        sphere(g, 0.047, x, 1.735, 0.415, eye, 10)
        sphere(g, 0.17, x * 2.1, 1.99, -0.08, fur, 12)
    }
    // little fur tufts and brows make the bear feel designed, not assembled
    sphere(g, 0.12, -0.12, 2.02, -0.05, fur, 10)
    sphere(g, 0.12, 0.12, 2.02, -0.05, fur, 10)
    box(g, 0.18, 0.045, 0.06, -0.19, 1.84, 0.35, dark)
    box(g, 0.18, 0.045, 0.06, 0.19, 1.84, 0.35, dark)
    box(g, 0.32, 0.32, 0.7, -0.75, 0.85, 0.25, fur)
    box(g, 0.32, 0.32, 0.7, 0.75, 0.85, 0.25, fur)
    sphere(g, 0.18, -0.45, 0.55, 0.55, fur, 10)
    sphere(g, 0.18, 0.45, 0.55, 0.55, fur, 10)
    box(g, 0.28, 0.28, 0.5, -0.28, 0.35, 0.35, fur)
    box(g, 0.28, 0.28, 0.5, 0.28, 0.35, 0.35, fur)
    box(g, 0.28, 0.14, 0.4, -0.28, 0.18, 0.65, dark)
    box(g, 0.28, 0.14, 0.4, 0.28, 0.18, 0.65, dark)
    box(g, 0.7, 0.12, 0.12, 0, 1.95, 0.05, accent)
}

/** Gohan — pequenininho, mecânico com chave inglesa. */
function styleMechanic(g, bodyMat, accent, dark) {
    const skin = bodyMat
    const suit = new THREE.MeshStandardMaterial({ color: 0x243e59, roughness: 0.72 })
    const oil = new THREE.MeshStandardMaterial({ color: 0x262a2e, metalness: 0.25, roughness: 0.45 })
    const patch = new THREE.MeshStandardMaterial({ color: 0xf0a52b, roughness: 0.55 })
    box(g, 0.38, 0.4, 0.3, 0, 0.48, -0.05, suit)
    box(g, 0.12, 0.22, 0.035, 0, 0.5, 0.12, patch)
    sphere(g, 0.17, 0, 0.79, -0.02, skin, 16)
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.1, 12), accent)
    cap.position.set(0, 0.9, -0.02)
    cap.castShadow = true
    g.add(cap)
    box(g, 0.18, 0.03, 0.12, 0, 0.86, 0.14, accent)
    // oversized mechanic goggles and a small stitched cap badge
    box(g, 0.3, 0.075, 0.055, 0, 0.8, 0.135, oil)
    sphere(g, 0.035, -0.075, 0.8, 0.17, patch, 8)
    box(g, 0.12, 0.055, 0.035, 0.0, 0.61, 0.135, patch)
    box(g, 0.09, 0.09, 0.28, -0.24, 0.48, 0.12, suit)
    box(g, 0.09, 0.09, 0.28, 0.24, 0.48, 0.12, suit)
    sphere(g, 0.07, -0.2, 0.42, 0.32, skin, 8)
    sphere(g, 0.07, 0.2, 0.42, 0.32, skin, 8)
    box(g, 0.06, 0.06, 0.35, 0.28, 0.42, 0.45, oil)
    box(g, 0.12, 0.08, 0.08, 0.28, 0.42, 0.62, oil)
    box(g, 0.12, 0.12, 0.28, -0.1, 0.25, 0.12, suit)
    box(g, 0.12, 0.12, 0.28, 0.1, 0.25, 0.12, suit)
    box(g, 0.11, 0.08, 0.16, -0.1, 0.16, 0.3, dark)
    box(g, 0.11, 0.08, 0.16, 0.1, 0.16, 0.3, dark)
    box(g, 0.1, 0.08, 0.06, 0.12, 0.5, 0.1, oil)
}

/** Artur — whale de skin, jaqueta e viseira neon. */
function styleNeonrider(g, bodyMat, accent, dark) {
    const skin = bodyMat
    const jacket = new THREE.MeshStandardMaterial({ color: 0x111522, roughness: 0.42, metalness: 0.22 })
    const neonC = new THREE.MeshStandardMaterial({ color: 0x00f0ff, emissive: 0x00dfff, emissiveIntensity: 2.0, metalness: 0.35, roughness: 0.22 })
    const neonP = new THREE.MeshStandardMaterial({ color: 0xff2d95, emissive: 0xff1688, emissiveIntensity: 1.8, metalness: 0.25, roughness: 0.25 })
    box(g, 0.5, 0.55, 0.38, 0, 0.6, -0.05, jacket)
    box(g, 0.52, 0.06, 0.06, 0, 0.75, 0.15, neonC)
    box(g, 0.52, 0.06, 0.06, 0, 0.55, 0.15, neonP)
    sphere(g, 0.2, 0, 1.0, -0.02, skin, 12)
    box(g, 0.42, 0.12, 0.18, 0, 1.05, 0.12, dark)
    box(g, 0.38, 0.06, 0.04, 0, 1.05, 0.22, neonC)
    box(g, 0.36, 0.14, 0.28, 0, 1.18, -0.08, toon(0x111111))
    box(g, 0.32, 0.035, 0.025, 0, 0.39, 0.16, neonC)
    box(g, 0.08, 0.26, 0.06, -0.18, 0.62, 0.16, neonP)
    box(g, 0.08, 0.26, 0.06, 0.18, 0.62, 0.16, neonC)
    box(g, 0.12, 0.12, 0.38, -0.35, 0.58, 0.15, jacket)
    box(g, 0.12, 0.12, 0.38, 0.35, 0.58, 0.15, jacket)
    box(g, 0.14, 0.08, 0.08, -0.35, 0.5, 0.35, neonP)
    box(g, 0.14, 0.08, 0.08, 0.35, 0.5, 0.35, neonC)
    sphere(g, 0.08, -0.28, 0.48, 0.4, skin, 8)
    sphere(g, 0.08, 0.28, 0.48, 0.4, skin, 8)
    box(g, 0.15, 0.15, 0.4, -0.14, 0.28, 0.15, dark)
    box(g, 0.15, 0.15, 0.4, 0.14, 0.28, 0.15, dark)
    box(g, 0.14, 0.1, 0.22, -0.14, 0.18, 0.4, neonC)
    box(g, 0.14, 0.1, 0.22, 0.14, 0.18, 0.4, neonP)
}

const STYLES = {
    default: styleDefault,
    bear: styleBear,
    mechanic: styleMechanic,
    neonrider: styleNeonrider,
}

export function makeProceduralCharacter(color = 0xffcc88, skinUrl, scale = 1, style = 'default') {
    const g = new THREE.Group()
    const bodyMat = skinUrl
        ? toon(0xffffff, { map: loadSkinTexture(skinUrl) })
        : toon(color)
    const dark = toon(0x222222)
    const accent = toon(color)
    const fn = STYLES[style] || STYLES.default
    fn(g, bodyMat, accent, dark)
    g.scale.setScalar(scale)
    // The pilot is seated in the cockpit; these offsets align feet and torso with the vehicle.
    if (style === 'bear') { g.scale.setScalar(0.78 * scale); g.position.set(0, 0.22, -0.22) }
    else if (style === 'mechanic') { g.scale.setScalar(0.92 * scale); g.position.set(0, 0.16, -0.2) }
    else if (style === 'neonrider') { g.scale.setScalar(0.86 * scale); g.position.set(0, 0.2, -0.18) }
    else g.scale.setScalar(scale)
    return { group: g, tint: skinUrl ? [accent] : [bodyMat, accent] }
}

export function disposeVisual(obj) {
    if (!obj) return
    obj.traverse((o) => {
        o.geometry?.dispose?.()
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : []
        mats.forEach((m) => m.dispose?.())
    })
}
